import { useState } from 'react'
import { useToast } from '../../hooks/useToast'
import { supabase } from '../../lib/supabase'

/**
 * My Account Tab
 * - Username (editable)
 * - Email (change email flow)
 * - Password (change password flow)
 * - 2FA section (Coming Soon)
 * - Account creation date
 * - Delete account (red button, bottom)
 */
export default function MyAccountTab({ user, profile, onUpdateProfile }) {
  const toast = useToast()
  const [isEditingUsername, setIsEditingUsername] = useState(false)
  const [username, setUsername] = useState(profile?.username || '')
  const [loading, setLoading] = useState(false)

  const handleUpdateUsername = async () => {
    if (!username.trim()) {
      toast.error('Username cannot be empty')
      return
    }

    setLoading(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ username: username.trim() })
        .eq('id', user.id)

      if (error) throw error

      onUpdateProfile({ ...profile, username: username.trim() })
      toast.success('Username updated successfully!')
      setIsEditingUsername(false)
    } catch (error) {
      console.error('Error updating username:', error)
      toast.error('Failed to update username')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="p-6 max-w-4xl">
      <h3 className="text-xl font-semibold text-white mb-6">My Account</h3>

      {/* Username */}
      <div className="mb-8 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <label className="block text-sm font-medium text-[#94a3b8] mb-2">Username</label>
        <div className="flex items-center gap-3">
          {isEditingUsername ? (
            <>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="flex-1 px-3 py-2 bg-[#0f172a] border border-[#1e3a5f] rounded text-white focus:border-[#00ad9f] focus:outline-none transition-colors"
                placeholder="Enter username"
              />
              <button
                onClick={handleUpdateUsername}
                disabled={loading}
                className="px-4 py-2 bg-[#00ad9f] hover:bg-[#00897b] text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setIsEditingUsername(false)
                  setUsername(profile?.username || '')
                }}
                className="px-4 py-2 bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white rounded text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <div className="flex-1 text-white font-medium">@{profile?.username || 'username'}</div>
              <button
                onClick={() => setIsEditingUsername(true)}
                className="px-4 py-2 bg-[#00ad9f] hover:bg-[#00897b] text-white rounded text-sm font-medium transition-colors"
              >
                Change Username
              </button>
            </>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="mb-8 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <label className="block text-sm font-medium text-[#94a3b8] mb-2">Email</label>
        <div className="flex items-center gap-3">
          <div className="flex-1 text-white">{user?.email || 'no-email@example.com'}</div>
          <button
            onClick={() => toast.info('Change email feature coming soon')}
            className="px-4 py-2 bg-[#00ad9f] hover:bg-[#00897b] text-white rounded text-sm font-medium transition-colors"
          >
            Change Email
          </button>
        </div>
        <p className="text-xs text-[#64748b] mt-2">
          Your email is used for account recovery and important notifications
        </p>
      </div>

      {/* Password */}
      <div className="mb-8 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <label className="block text-sm font-medium text-[#94a3b8] mb-2">Password</label>
        <div className="flex items-center gap-3">
          <div className="flex-1 text-white">••••••••••••</div>
          <button
            onClick={() => toast.info('Change password feature coming soon')}
            className="px-4 py-2 bg-[#00ad9f] hover:bg-[#00897b] text-white rounded text-sm font-medium transition-colors"
          >
            Change Password
          </button>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="mb-8 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <label className="block text-sm font-medium text-[#94a3b8] mb-2">Two-Factor Authentication</label>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white">Not enabled</p>
            <p className="text-xs text-[#64748b] mt-1">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="px-3 py-1 bg-[#00ad9f]/20 text-[#00ad9f] rounded text-xs font-semibold">
            Coming Soon
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="mb-8 p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <label className="block text-sm font-medium text-[#94a3b8] mb-3">Account Information</label>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-[#64748b]">Member Since</span>
            <span className="text-sm text-white font-medium">
              {formatDate(profile?.created_at || user?.created_at)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#64748b]">User ID</span>
            <span className="text-sm text-white font-mono text-xs">
              {user?.id?.slice(0, 8)}...
            </span>
          </div>
        </div>
      </div>

      {/* Delete Account */}
      <div className="mt-12 pt-8 border-t border-[#1e3a5f]">
        <h4 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide mb-4">
          Danger Zone
        </h4>
        <div className="p-4 bg-[#ED4245]/10 border border-[#ED4245]/30 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h5 className="text-sm font-medium text-[#ED4245] mb-1">Delete Account</h5>
              <p className="text-xs text-[#64748b]">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
            </div>
            <button
              onClick={() => toast.warning('Account deletion is not available yet. Please contact support.')}
              className="ml-4 px-4 py-2 bg-[#ED4245] hover:bg-[#A12D30] text-white rounded text-sm font-medium transition-colors"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
